/**
 * Profanity Filter for User Input
 * Checks first names and user content for inappropriate language
 */

// Comprehensive list of profanity and inappropriate terms
const PROFANITY_LIST = [
  // Common profanity
  'fuck', 'shit', 'bitch', 'ass', 'asshole', 'bastard', 'damn', 'hell',
  'crap', 'piss', 'dick', 'cock', 'pussy', 'cunt', 'whore', 'slut',
  'fag', 'faggot', 'retard', 'nigger', 'nigga', 'chink', 'spic', 'kike',
  
  // Variations and leetspeak
  'f*ck', 'fuk', 'fck', 'sh!t', 'b!tch', 'a$$', '@ss', 'd!ck', 'c0ck',
  'pu$$y', 'b1tch', 'sh1t', 'fvck', 'phuck', 'azz', 'biatch',
  
  // Slurs and hate speech
  'nazi', 'hitler', 'rape', 'molest', 'terrorist', 'jihad',
  
  // Sexual content
  'porn', 'xxx', 'sex', 'sexy', 'nude', 'naked', 'penis', 'vagina',
  'orgasm', 'masturbate', 'horny', 'anal', 'oral', 'bj', 'blowjob',
  
  // Drugs
  'cocaine', 'heroin', 'meth', 'weed', 'marijuana', 'crack',
  
  // Scam/spam indicators
  'bitcoin', 'crypto', 'lottery', 'winner', 'prize', 'congratulations',
  'click here', 'buy now', 'limited time', 'act fast'
];

// Inappropriate name patterns (common joke names, celebrities used inappropriately)
const INAPPROPRIATE_NAMES = [
  'admin', 'administrator', 'moderator', 'support', 'fidora',
  'bot', 'ai', 'assistant', 'coach', 'system', 'root',
  'test', 'testing', 'null', 'undefined', 'anonymous', 'guest'
];

/**
 * Check if text contains profanity
 * @param {string} text - Text to check
 * @returns {boolean} - True if profanity found
 */
export function containsProfanity(text) {
  if (!text || typeof text !== 'string') return false;
  
  const lowerText = text.toLowerCase().trim();
  
  // Check against profanity list - EXACT WORD BOUNDARIES ONLY
  for (const word of PROFANITY_LIST) {
    // Use word boundaries (\b) for exact word matching only
    // This prevents "classical" from matching "ass" or "assassin" from matching "ass"
    const regex = new RegExp(`\\b${word.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\b`, 'i');
    if (regex.test(lowerText)) {
      return true;
    }
  }
  
  return false;
}

/**
 * Validate first name for registration
 * @param {string} firstName - First name to validate
 * @returns {object} - {valid: boolean, error: string}
 */
export function validateFirstName(firstName) {
  if (!firstName || typeof firstName !== 'string') {
    return { valid: false, error: 'First name is required' };
  }
  
  const trimmed = firstName.trim();
  
  // Length checks
  if (trimmed.length === 0) {
    return { valid: false, error: 'First name cannot be empty' };
  }
  
  if (trimmed.length < 2) {
    return { valid: false, error: 'First name must be at least 2 characters' };
  }
  
  if (trimmed.length > 50) {
    return { valid: false, error: 'First name is too long (max 50 characters)' };
  }
  
  // Only letters, spaces, hyphens, and apostrophes
  const namePattern = /^[a-zA-Z\s'-]+$/;
  if (!namePattern.test(trimmed)) {
    return { valid: false, error: 'First name can only contain letters, spaces, hyphens, and apostrophes' };
  }
  
  // Check for profanity
  if (containsProfanity(trimmed)) {
    return { valid: false, error: 'Please use an appropriate name' };
  }
  
  // Check against inappropriate names
  const lowerName = trimmed.toLowerCase();
  for (const inappropriate of INAPPROPRIATE_NAMES) {
    if (lowerName === inappropriate || lowerName.includes(inappropriate)) {
      return { valid: false, error: 'Please use your real first name' };
    }
  }
  
  // Check for repeated characters (e.g., "aaaaaaa")
  const repeatedPattern = /(.)\1{4,}/;
  if (repeatedPattern.test(trimmed)) {
    return { valid: false, error: 'Please use a valid name' };
  }
  
  // Check for numbers
  if (/\d/.test(trimmed)) {
    return { valid: false, error: 'First name cannot contain numbers' };
  }
  
  // Check for excessive punctuation
  const punctuationCount = (trimmed.match(/['-]/g) || []).length;
  if (punctuationCount > 2) {
    return { valid: false, error: 'Too many special characters in name' };
  }
  
  return { valid: true, error: null };
}

/**
 * Sanitize and format first name
 * @param {string} firstName - First name to sanitize
 * @returns {string} - Sanitized first name
 */
export function sanitizeFirstName(firstName) {
  if (!firstName || typeof firstName !== 'string') return '';
  
  return firstName
    .trim()
    .split(' ')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
}

/**
 * Get a safe display name for UI (filters profanity and formats)
 * @param {string} displayName
 * @returns {string}
 */
export function getSafeDisplayName(displayName) {
  if (!displayName || typeof displayName !== 'string') return 'User';
  const trimmed = displayName.trim();
  if (trimmed.length === 0) return 'User';
  if (containsProfanity(trimmed)) return 'User';
  // Format each word nicely
  return trimmed
    .split(' ')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
}

/**
 * Check if message content is appropriate
 * @param {string} message - Message to check
 * @returns {boolean} - True if appropriate
 */
export function isMessageAppropriate(message) {
  if (!message || typeof message !== 'string') return false;
  
  // Check for profanity
  if (containsProfanity(message)) {
    return false;
  }
  
  // Check for spam patterns
  const spamPatterns = [
    /click\s+here/i,
    /buy\s+now/i,
    /limited\s+time/i,
    /act\s+fast/i,
    /congratulations/i,
    /you've\s+won/i,
    /\$\$\$/,
    /💰{3,}/,
    /(http|https):\/\//i // URLs (can be spam)
  ];
  
  for (const pattern of spamPatterns) {
    if (pattern.test(message)) {
      return false;
    }
  }
  
  return true;
}

export default {
  containsProfanity,
  validateFirstName,
  sanitizeFirstName,
  isMessageAppropriate
};

